import { useQuery } from '@tanstack/react-query';
import { 
  Users, 
  DollarSign, 
  TrendingUp, 
  Clock,
  Heart,
  Smile,
  ShoppingBag,
  Fuel,
  Gift,
  Download,
  Calendar,
  PieChart
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';
import { formatCurrency } from '../../lib/utils';

interface DashboardData {
  totalSubscriptions: number;
  activeSubscriptions: number;
  pendingApprovals: number;
  cancelledThisMonth: number;
  totalMonthlyCost: string;
  totalSubsidy: string;
  netCost: string;
  byCategory: Array<{
    category: string;
    count: number;
    totalCost: string;
  }>;
  byPlan: Array<{
    planId: string;
    planName: string;
    count: number;
    totalCost: string;
  }>;
  recentActivity: Array<{
    id: string;
    type: string;
    userName: string;
    planName: string;
    date: string;
  }>;
}

const categoryIcons: Record<string, React.ReactNode> = {
  HEALTH: <Heart className="w-5 h-5" />,
  DENTAL: <Smile className="w-5 h-5" />,
  SUPERMARKET: <ShoppingBag className="w-5 h-5" />,
  GAS_STATION: <Fuel className="w-5 h-5" />,
  GIFT_CARD: <Gift className="w-5 h-5" />,
};

const categoryLabels: Record<string, string> = {
  HEALTH: 'Plano de Saúde',
  DENTAL: 'Plano Odontológico',
  SUPERMARKET: 'Supermercados',
  GAS_STATION: 'Postos',
  GIFT_CARD: 'Vale Presente',
  MEAL: 'Vale Refeição',
  PHARMACY: 'Farmácia',
  EDUCATION: 'Educação',
  WELLNESS: 'Bem-estar',
  OTHER: 'Outros',
};

const categoryColors: Record<string, string> = {
  HEALTH: 'bg-red-500',
  DENTAL: 'bg-blue-500',
  SUPERMARKET: 'bg-green-500',
  GAS_STATION: 'bg-orange-500',
  GIFT_CARD: 'bg-purple-500',
  MEAL: 'bg-yellow-500',
  PHARMACY: 'bg-pink-500',
  EDUCATION: 'bg-indigo-500',
  WELLNESS: 'bg-teal-500',
  OTHER: 'bg-gray-500',
};

export function BenefitsDashboard() {
  const { data: dashboard, isLoading } = useQuery<DashboardData>({
    queryKey: ['admin-benefits-dashboard'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.benefits.adminDashboard);
      return response.data;
    },
  });

  const handleExportPayroll = async () => {
    try {
      const response = await api.get(ENDPOINTS.benefits.adminPayroll, {
        responseType: 'blob',
        params: { format: 'csv' },
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `folha-beneficios-${new Date().toISOString().slice(0, 7)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao exportar folha:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-surface-700 rounded w-48 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <div className="h-20 bg-surface-700 rounded" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const totalCost = Number(dashboard?.totalMonthlyCost || 0) / 100;
  const totalSubsidy = Number(dashboard?.totalSubsidy || 0) / 100;
  const netCost = Number(dashboard?.netCost || 0) / 100;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard de Benefícios</h1>
          <p className="text-surface-400 mt-1">
            Visão geral dos benefícios e custos do tenant
          </p>
        </div>
        <Button onClick={handleExportPayroll}>
          <Download className="w-4 h-4 mr-2" />
          Exportar Folha
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-primary-500/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <p className="text-sm text-surface-400">Adesões Ativas</p>
              <p className="text-2xl font-bold text-white">
                {dashboard?.activeSubscriptions || 0}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <p className="text-sm text-surface-400">Aguardando Aprovação</p>
              <p className="text-2xl font-bold text-white">
                {dashboard?.pendingApprovals || 0}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-surface-400">Custo Mensal Total</p>
              <p className="text-2xl font-bold text-white">
                {formatCurrency(totalCost)}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-surface-400">Subsídio Total</p>
              <p className="text-2xl font-bold text-green-400">
                {formatCurrency(totalSubsidy)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Cost Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-primary-400" />
            Distribuição por Categoria
          </h3>
          
          {dashboard?.byCategory && dashboard.byCategory.length > 0 ? (
            <div className="space-y-4">
              {dashboard.byCategory.map((cat) => {
                const percentage = dashboard.activeSubscriptions > 0
                  ? (cat.count / dashboard.activeSubscriptions) * 100
                  : 0;
                return (
                  <div key={cat.category}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-primary-400">
                          {categoryIcons[cat.category] || <Gift className="w-4 h-4" />}
                        </span>
                        <span className="text-surface-300">
                          {categoryLabels[cat.category] || cat.category}
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-surface-400">{cat.count} adesões</span>
                        <span className="text-white font-medium">
                          {formatCurrency(Number(cat.totalCost) / 100)}
                        </span>
                      </div>
                    </div>
                    <div className="h-2 bg-surface-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${categoryColors[cat.category] || 'bg-gray-500'} transition-all`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-surface-400 text-center py-8">
              Nenhuma adesão ativa
            </p>
          )}
        </Card>

        {/* Cost Summary */}
        <Card>
          <h3 className="text-lg font-semibold text-white mb-4">Resumo de Custos</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-surface-700">
              <span className="text-surface-400">Custo Bruto</span>
              <span className="text-white font-medium">{formatCurrency(totalCost)}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-surface-700">
              <span className="text-surface-400">Subsídio</span>
              <span className="text-green-400 font-medium">-{formatCurrency(totalSubsidy)}</span>
            </div>
            <div className="flex justify-between items-center py-3">
              <span className="text-white font-medium">Custo Líquido</span>
              <span className="text-primary-400 text-xl font-bold">{formatCurrency(netCost)}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Top Plans */}
      <Card>
        <h3 className="text-lg font-semibold text-white mb-4">Planos Mais Populares</h3>
        {dashboard?.byPlan && dashboard.byPlan.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-700">
                  <th className="text-left py-3 px-4 text-surface-400 font-medium">Plano</th>
                  <th className="text-center py-3 px-4 text-surface-400 font-medium">Adesões</th>
                  <th className="text-right py-3 px-4 text-surface-400 font-medium">Custo Total</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.byPlan.slice(0, 5).map((plan, index) => (
                  <tr key={plan.planId} className="border-b border-surface-800">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className="text-surface-500">{index + 1}.</span>
                        <span className="text-white">{plan.planName}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Badge variant="info">{plan.count}</Badge>
                    </td>
                    <td className="py-3 px-4 text-right text-white font-medium">
                      {formatCurrency(Number(plan.totalCost) / 100)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-surface-400 text-center py-8">
            Nenhum plano com adesões
          </p>
        )}
      </Card>

      {/* Recent Activity */}
      {dashboard?.recentActivity && dashboard.recentActivity.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary-400" />
            Atividade Recente
          </h3>
          <div className="space-y-3">
            {dashboard.recentActivity.map((activity) => (
              <div
                key={activity.id}
                className="flex items-center justify-between py-2 border-b border-surface-800 last:border-0"
              >
                <div className="flex items-center gap-3">
                  <Badge
                    variant={
                      activity.type === 'SUBSCRIPTION' ? 'success' :
                      activity.type === 'CANCELLATION' ? 'error' : 'info'
                    }
                    size="sm"
                  >
                    {activity.type === 'SUBSCRIPTION' ? 'Nova Adesão' :
                     activity.type === 'CANCELLATION' ? 'Cancelamento' : activity.type}
                  </Badge>
                  <span className="text-white">{activity.userName}</span>
                  <span className="text-surface-400">→</span>
                  <span className="text-surface-300">{activity.planName}</span>
                </div>
                <span className="text-sm text-surface-500">
                  {new Date(activity.date).toLocaleDateString('pt-BR')}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

export default BenefitsDashboard;
